import React, { useEffect, useRef, useState } from 'react';
import {
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Typography,
  Box,
  CircularProgress,
  styled
} from '@mui/material';
import { UserService } from '../../api/generated/requests/services.gen';
import { CurrentUserInfoDTO } from '../../api/generated/requests/types.gen';

interface MentionInputProps {
  value: string;
  onChange: (value: string, mentionedUserIds: string[]) => void;
  placeholder?: string;
  multiline?: boolean;
  rows?: number;
  variant?: 'outlined' | 'filled' | 'standard';
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  sx?: any;
}

interface MentionData {
  id: string;
  displayName: string;
  startIndex: number;
  endIndex: number;
}

const StyledMentionContainer = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  minHeight: '56px',
  display: 'flex',
  alignItems: 'flex-start',
  cursor: 'text',
  backgroundColor: theme.palette.background.paper,
  '&:focus-within': {
    borderColor: theme.palette.primary.main,
    borderWidth: '2px',
  },
  '&.small': {
    minHeight: '40px',
  }
}));

const MentionChip = styled('span')(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 500,
  backgroundColor: 'rgba(25, 118, 210, 0.08)',
  borderRadius: 4,
  padding: '0 2px',
  margin: '0 1px',
  display: 'inline-block',
  userSelect: 'none',
  pointerEvents: 'auto',
  cursor: 'pointer',
}));

// Helper: Converts value string to segments with mentions and text
function parseTextWithMentions(value: string) {
  const mentionRegex = /@\[([^\]]+)\]\(([^)]+)\)/g;
  const segments: Array<{ text: string, isMention?: boolean, mentionData?: MentionData }> = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = mentionRegex.exec(value)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ text: value.substring(lastIndex, match.index) });
    }
    segments.push({
      text: match[1],
      isMention: true,
      mentionData: {
        displayName: match[1],
        id: match[2],
        startIndex: match.index,
        endIndex: match.index + match[0].length,
      }
    });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < value.length) {
    segments.push({ text: value.substring(lastIndex) });
  }
  return segments;
}

// Helper: Converts DOM to value string
function domToValue(root: HTMLElement) {
  let value = '';
  root.childNodes.forEach(node => {
    if (node.nodeType === Node.TEXT_NODE) {
      value += node.textContent || '';
    } else if ((node as HTMLElement).dataset && (node as HTMLElement).dataset.mentionId) {
      const span = node as HTMLElement;
      const displayName = span.innerText;
      const id = span.dataset.mentionId;
      value += `@[${displayName}](${id})`;
    } else if (node.nodeName === 'BR') {
      value += '\n';
    }
  });
  return value;
}

// Helper: Sets caret position at end
function setCaretToEnd(el: HTMLElement) {
  const range = document.createRange();
  range.selectNodeContents(el);
  range.collapse(false);
  const sel = window.getSelection();
  if (sel) {
    sel.removeAllRanges();
    sel.addRange(range);
  }
}

export const MentionInput: React.FC<MentionInputProps> = ({
                                                            value,
                                                            onChange,
                                                            placeholder = "Wpisz komentarz...",
                                                            multiline = true,
                                                            rows = 3,
                                                            size = 'small',
                                                            fullWidth = true,
                                                            sx = {}
                                                          }) => {
  const [users, setUsers] = useState<CurrentUserInfoDTO[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<CurrentUserInfoDTO[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionStart, setMentionStart] = useState<number | null>(null);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);

  // === USERS LOADING ===
  useEffect(() => {
    const loadUsers = async () => {
      setLoadingUsers(true);
      try {
        const resp = await UserService.getApiUserDomain();
        setUsers(resp);
      } catch (e) { }
      setLoadingUsers(false);
    };
    loadUsers();
  }, []);

  // === SUGGESTIONS LOGIC ===
  useEffect(() => {
    if (!showSuggestions) return;
    if (!mentionQuery) {
      setFilteredUsers(users);
    } else {
      const q = mentionQuery.toLowerCase();
      setFilteredUsers(users.filter(user => {
        const fullName = `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase();
        const email = (user.email || '').toLowerCase();
        return fullName.includes(q) || email.includes(q);
      }));
    }
    setSelectedIndex(0);
  }, [mentionQuery, users, showSuggestions]);

  // === VALUE SYNC TO DOM ===
  useEffect(() => {
    // Avoid unnecessary rerenders if content is already up to date
    const editor = editorRef.current;
    if (!editor) return;
    // Do not override user's input if the content matches (avoid caret jumps)
    const domValue = domToValue(editor);
    if (domValue !== value) {
      // Render value as spans and text
      editor.innerHTML = '';
      for (const seg of parseTextWithMentions(value)) {
        if (seg.isMention && seg.mentionData) {
          const span = document.createElement('span');
          span.innerText = seg.mentionData.displayName;
          span.contentEditable = 'false';
          span.dataset.mentionId = seg.mentionData.id;
          span.className = 'mention-chip';
          span.style.cssText = `
            color: #1976d2;
            font-weight: 500;
            background: rgba(25, 118, 210, 0.08);
            border-radius: 4px;
            padding: 0 2px;
            margin: 0 1px;
            display: inline-block;
            user-select: none;
            pointer-events: auto;
          `;
          editor.appendChild(span);
        } else {
          // Replace "\n" with <br/>
          const parts = seg.text.split('\n');
          for (let i = 0; i < parts.length; i++) {
            if (parts[i]) editor.appendChild(document.createTextNode(parts[i]));
            if (i < parts.length - 1) editor.appendChild(document.createElement('br'));
          }
        }
      }
    }
  }, [value]);

  // === ON INPUT LOGIC ===
  const handleInput = (e: React.FormEvent<HTMLDivElement>) => {
    const editor = e.currentTarget;
    const newValue = domToValue(editor);
    onChange(newValue, extractMentionIds(newValue));

    // Mention: check if just typed @
    const selection = window.getSelection();
    if (selection && selection.anchorNode) {
      const textBefore = getTextBeforeCursor(selection);
      const atIndex = textBefore.lastIndexOf('@');
      if (atIndex !== -1 && (atIndex === 0 || /\s/.test(textBefore[atIndex - 1]))) {
        const afterAt = textBefore.substring(atIndex + 1);
        if (!/\s/.test(afterAt)) {
          setMentionStart(atIndex);
          setMentionQuery(afterAt);
          setShowSuggestions(true);
          return;
        }
      }
    }
    setShowSuggestions(false);
    setMentionStart(null);
    setMentionQuery('');
  };

  // === HANDLE KEY EVENTS ===
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (showSuggestions && filteredUsers.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(i => (i + 1) % filteredUsers.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(i => (i - 1 + filteredUsers.length) % filteredUsers.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertMention(filteredUsers[selectedIndex]);
        return;
      }
      if (e.key === 'Escape') {
        setShowSuggestions(false);
        setMentionQuery('');
        setMentionStart(null);
        return;
      }
    }
    // Protect mention chip (delete as whole, not inside)
    if ((e.key === 'Backspace' || e.key === 'Delete') && editorRef.current) {
      const sel = window.getSelection();
      if (sel && sel.anchorNode && sel.anchorNode.nodeType === 1) {
        const node = sel.anchorNode as HTMLElement;
        if (node.dataset && node.dataset.mentionId) {
          e.preventDefault();
          // Remove mention chip
          node.remove();
          handleInput({ currentTarget: editorRef.current } as any);
          return;
        }
      }
    }
  };

  // === INSERT MENTION ===
  function insertMention(user: CurrentUserInfoDTO) {
    const editor = editorRef.current;
    if (!editor) return;
    const selection = window.getSelection();
    if (!selection || !selection.anchorNode) return;

    // Find current @... query range
    const range = selection.getRangeAt(0);
    let text = getTextBeforeCursor(selection);
    const atIndex = text.lastIndexOf('@');
    if (atIndex === -1) return;
    // Remove @query and insert chip
    for (let i = 0; i < text.length - atIndex; i++) {
      range.setStart(range.startContainer, range.startOffset - 1);
      range.deleteContents();
    }
    // Insert chip
    const displayName = getUserDisplayName(user);
    const span = document.createElement('span');
    span.innerText = displayName;
    span.contentEditable = 'false';
    span.dataset.mentionId = user.id;
    span.className = 'mention-chip';
    span.style.cssText = `
      color: #1976d2;
      font-weight: 500;
      background: rgba(25, 118, 210, 0.08);
      border-radius: 4px;
      padding: 0 2px;
      margin: 0 1px;
      display: inline-block;
      user-select: none;
      pointer-events: auto;
    `;
    range.insertNode(span);
    range.setStartAfter(span);
    range.collapse(true);

    // Insert space after mention for UX
    range.insertNode(document.createTextNode(' '));
    range.setStart(range.startContainer, range.startOffset + 1);
    range.collapse(true);

    // Notify React
    handleInput({ currentTarget: editor } as any);

    setShowSuggestions(false);
    setMentionQuery('');
    setMentionStart(null);

    // Focus back, caret after chip
    setTimeout(() => {
      if (editorRef.current) editorRef.current.focus();
    }, 0);
  }

  // === HELPERS ===
  function extractMentionIds(val: string) {
    const re = /@\[([^\]]+)\]\(([^)]+)\)/g;
    const arr: string[] = [];
    let match;
    while ((match = re.exec(val)) !== null) arr.push(match[2]);
    return arr;
  }
  function getUserDisplayName(user: CurrentUserInfoDTO): string {
    return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'Unknown';
  }
  function getUserInitials(user: CurrentUserInfoDTO): string {
    const firstName = user.firstName || '';
    const lastName = user.lastName || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  }
  function getTextBeforeCursor(selection: Selection): string {
    if (!selection.anchorNode) return '';
    let text = '';
    let node = selection.anchorNode;
    let offset = selection.anchorOffset;
    if (node.nodeType === Node.TEXT_NODE) {
      text = (node.textContent || '').substring(0, offset);
    }
    // Traverse backwards siblings for previous text/chips
    while (node && node.parentNode && node.parentNode !== editorRef.current) {
      let prev = node.previousSibling;
      while (prev) {
        if (prev.nodeType === Node.TEXT_NODE) {
          text = (prev.textContent || '') + text;
        } else if ((prev as HTMLElement).dataset && (prev as HTMLElement).dataset.mentionId) {
          text = (prev as HTMLElement).innerText + text;
        }
        prev = prev.previousSibling;
      }
      node = node.parentNode;
    }
    return text;
  }

  // === UI ===
  return (
      <Box sx={{ position: 'relative', ...sx }}>
        <StyledMentionContainer
            className={size === 'small' ? 'small' : ''}
            sx={{
              minHeight: multiline ? `${(rows || 3) * 24 + 16}px` : '40px',
            }}
            onClick={() => { editorRef.current?.focus(); }}
        >
          <div
              ref={editorRef}
              contentEditable
              suppressContentEditableWarning
              spellCheck
              role="textbox"
              aria-multiline={multiline}
              tabIndex={0}
              style={{
                outline: 'none',
                minHeight: '1.2em',
                fontSize: size === 'small' ? '0.875rem' : '1rem',
                width: '100%',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                padding: size === 'small' ? '6px 8px' : '8px 12px',
                background: 'transparent',
              }}
              onInput={handleInput}
              onKeyDown={handleKeyDown}
              data-placeholder={placeholder}
          />
          {/* Placeholder */}
          {!value && (
              <Typography
                  variant="body1"
                  sx={{
                    position: 'absolute',
                    left: size === 'small' ? 8 : 12,
                    top: size === 'small' ? 6 : 8,
                    color: 'text.secondary',
                    fontSize: 'inherit',
                    fontStyle: 'italic',
                    pointerEvents: 'none',
                    zIndex: 0,
                    userSelect: 'none'
                  }}
              >
                {placeholder}
              </Typography>
          )}
        </StyledMentionContainer>
        {showSuggestions && filteredUsers.length > 0 && (
            <Paper
                sx={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  zIndex: 1000,
                  maxHeight: 200,
                  overflow: 'auto',
                  mt: 0.5
                }}
            >
              {loadingUsers ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={20} />
                  </Box>
              ) : (
                  <List dense>
                    {filteredUsers.map((user, index) => (
                        <ListItem key={user.id} disablePadding>
                          <ListItemButton
                              selected={index === selectedIndex}
                              onClick={() => insertMention(user)}
                          >
                            <ListItemAvatar>
                              <Avatar
                                  src={user.avatarUrl}
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    backgroundColor: user.color || '#1976d2',
                                    fontSize: '0.75rem'
                                  }}
                              >
                                {getUserInitials(user)}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                                primary={
                                  <Typography variant="body2">
                                    {getUserDisplayName(user)}
                                  </Typography>
                                }
                                secondary={
                                  <Typography variant="caption" color="text.secondary">
                                    {user.email}
                                  </Typography>
                                }
                            />
                          </ListItemButton>
                        </ListItem>
                    ))}
                  </List>
              )}
            </Paper>
        )}
      </Box>
  );
};